<template>
  <el-dialog
    :title="dialogType === 'add' ? $t('ADD_CATEGORY') : $t('EDIT_CATEGORY')"
    :visible.sync="dialogVisible"
    width="50%"
    append-to-body
  >
    <el-form :model="categoryForm" label-width="200px">
      <el-form-item :label="$t('CATEGORY_NAME')" prop="name">
        <el-input v-model="categoryForm.name"></el-input>
      </el-form-item>
      <el-form-item :label="$t('COMMON_THUMBNAIL') || 'Thumbnail'" v-if="!categoryForm.parentId">
        <BaseInputQuestionFile
          accept="image/*"
          :labelSize="14"
          :loading.sync="uploadLoading.thumbnail"
          v-model="categoryForm.thumbnail"
          iconClass="image"
          :storageClass="config ? config.storageClass : ''"
          :file="file.thumbnail"
          @file-selected="(val) => handleAttachmentUpload('thumbnail', val)"
          @clear="clearAttachment('thumbnail')"
        />
      </el-form-item>
      <el-form-item :label="$t('CATEGORY_INDEX')" prop="index">
        <el-input-number
          v-model="categoryForm.index"
          :min="1"
          :step="1"
          step-strictly
          controls-position="right"
        ></el-input-number>
        <div class="category-index-hint">{{ $t('CATEGORY_INDEX_HINT') }}</div>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">{{ $t('CANCEL') }}</el-button>
      <el-button
        type="primary"
        @click.prevent="submitCategoryForm"
        :loading="isSubmitting"
        :disabled="isSubmitting"
      >
        {{ $t('CONFIRM') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex'
import examVaultApi from '@/api/exam-vault'

export default {
  name: 'CategoryDialog',

  props: {
    ownerCategory: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      dialogVisible: false,
      dialogType: 'add',
      isSubmitting: false,
      categoryForm: {
        name: '',
        code: '',
        parentId: null,
        index: 1,
        thumbnail: null
      },
      // Danh mục đang sửa (chỉ có khi edit)
      currentCategory: null,
      uploadLoading: { thumbnail: false },
      uploadProgress: { thumbnail: 0 },
      file: { thumbnail: null }
    }
  },

  computed: {
    ...mapState('exam-vault', ['config'])
  },

  methods: {
    openAdd(parentId = null) {
      this.dialogType = 'add'
      this.categoryForm = { name: '', code: '', parentId, index: 1, thumbnail: null }
      this.file.thumbnail = null
      this.currentCategory = null
      this.dialogVisible = true
    },

    openEdit(category) {
      this.dialogType = 'edit'
      this.categoryForm = {
        name: category.name,
        code: category.code || '',
        parentId: category.parentId,
        // index 0 / không có = chưa đặt thứ tự -> để trống
        index: category.index >= 1 ? category.index : undefined,
        thumbnail: category.thumbnail || null
      }
      this.file.thumbnail = null
      this.currentCategory = category
      this.dialogVisible = true
    },

    async uploadThumbnail(categoryId) {
      this.$set(this.uploadLoading, 'thumbnail', true)
      try {
        const uploadRes = await examVaultApi.uploadCategoryAttachment(
          { categoryId, file: this.file.thumbnail },
          (progressEvent) => {
            this.uploadProgress.thumbnail = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        )
        const thumbnailKey = '/' + uploadRes.data.key
        await examVaultApi.updateCategory(categoryId, { thumbnail: thumbnailKey })
        this.$set(this.categoryForm, 'thumbnail', thumbnailKey)
        this.$set(this.file, 'thumbnail', uploadRes.data.key)
      } catch (uploadErr) {
        console.error(uploadErr)
        this.$notify({ type: 'error', title: 'Lỗi', message: 'Tải thumbnail thất bại' })
      }
      this.$set(this.uploadLoading, 'thumbnail', false)
    },

    async submitCategoryForm() {
      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        if (this.dialogType === 'add') {
          const res = await examVaultApi.createCategory({
            ownerCategory: this.ownerCategory,
            name: this.categoryForm.name,
            code: this.categoryForm.code,
            parentId: this.categoryForm.parentId,
            index: this.categoryForm.index ?? null,
            thumbnail: this.categoryForm.thumbnail
          })
          const categoryId = res.data && res.data._id ? res.data._id : null
          if (this.file.thumbnail instanceof File && categoryId) {
            await this.uploadThumbnail(categoryId)
          }
          this.$message.success(this.$t('CATEGORY_CREATED_SUCCESSFULLY'))
        } else {
          await examVaultApi.updateCategory(this.currentCategory._id, {
            ownerCategory: this.ownerCategory,
            name: this.categoryForm.name,
            code: this.categoryForm.code,
            index: this.categoryForm.index ?? null,
            thumbnail: this.categoryForm.thumbnail
          })
          if (this.file.thumbnail instanceof File) {
            await this.uploadThumbnail(this.currentCategory._id)
          }
          this.$message.success(this.$t('CATEGORY_UPDATED_SUCCESSFULLY'))
        }

        this.dialogVisible = false
        this.$emit('saved', { parentId: this.categoryForm.parentId || null })
      } catch (error) {
        // lỗi đã được request.js thông báo
      } finally {
        this.isSubmitting = false
      }
    },

    async handleAttachmentUpload(field, e) {
      if (!e || e.length === 0) return
      const file = e[0]

      if (this.currentCategory && this.currentCategory._id) {
        this.$set(this.uploadLoading, field, true)
        try {
          const response = await examVaultApi.uploadCategoryAttachment(
            { categoryId: this.currentCategory._id, file },
            (progressEvent) => {
              this.$set(this.uploadProgress, field, Math.round((progressEvent.loaded * 100) / progressEvent.total))
            }
          )
          this.$set(this.categoryForm, field, '/' + response.data.key)
          this.$set(this.file, field, response.data.key)
        } catch (err) {
          console.error(err)
          this.$notify({ type: 'error', title: 'Lỗi', message: 'Tải ảnh thất bại' })
        }
        this.$set(this.uploadLoading, field, false)
      } else {
        this.file[field] = file
        this.$set(this.categoryForm, field, URL.createObjectURL(file))
      }
    },

    clearAttachment(field) {
      this.file[field] = null
      this.$set(this.categoryForm, field, null)
    }
  }
}
</script>

<style lang="scss" scoped>
.category-index-hint {
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
  margin-top: 4px;
}
</style>
